import PDFViewer from '@/components/commonComponents/PDFViewer';
import { OrgType } from '@/enums';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import { useGetTenantQuery } from '@/hooks/useGetTenantQuery';
import { useToggleModal } from '@/hooks/useToggleModal';
import { ModalBody } from '@/pages/authenPages/Kit/section/styles';
import { AdminSettingEntity } from '@/pages/privatePages/user/types';
import { useAcceptTermsOfServiceMutation } from '@/queries';
import { newQueryKeys } from '@/queries/queryKeys';
import {
  useGetAdminSettingQuery,
  useGetPresignedUrlMutation,
} from '@/queries/user.query';
import { getToken } from '@/utils';
import { CustomButton, CustomModal } from '@amili-v3/amili-v3-design';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { get, isEmpty } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FirstLoginPromptModal.scss';

const FirstLoginPromptModal: React.FC = _props => {
  const { t } = useTranslation();
  const { firstLoginModal } = useToggleModal(['firstLogin']);
  const [pdfUrl, setPdfUrl] = useState<string>();

  const token = getToken('accessToken');

  const adminSettingData = useGetAdminSettingQuery({
    options: { enabled: !!token },
  });

  const adminSettings = get(adminSettingData, 'data', {} as AdminSettingEntity);

  const { termsOfServiceFileUrl } = adminSettings;
  const { path = '', uploadAt } = termsOfServiceFileUrl || {};
  const queryClient = useQueryClient();

  const getPresignedUrl = useGetPresignedUrlMutation({
    onSuccess(data, path, context) {
      setPdfUrl(data.url);
    },
  });

  const { tenant } = useGetTenantQuery();

  const { orgType = 'ROOT', id: tenantId } = tenant;

  const { data: user, isFetching } = useGetMe({
    enabled: !isEmpty(adminSettings),
  });

  useEffect(() => {
    if (
      adminSettingData.isFetched &&
      ['idle'].includes(getPresignedUrl.status) &&
      !!user &&
      !isEmpty(user)
    ) {
      const shouldShowModal =
        orgType === OrgType.CLINIC &&
        (!user.lastTermAgreeAt ||
          dayjs(user.lastTermAgreeAt) < dayjs(uploadAt));

      if (shouldShowModal && !firstLoginModal.visible) {
        getPresignedUrl.mutate(path);
        firstLoginModal.open();
      } else {
        firstLoginModal.close();
      }
    }
  }, [
    user,
    orgType,
    firstLoginModal,
    adminSettingData.isFetched,
    getPresignedUrl,
    path,
    uploadAt,
  ]);

  const acceptTerms = useAcceptTermsOfServiceMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.profile.invalidateAllKey,
      });
      firstLoginModal.close();
    },
  });

  const onAcceptTermsOfService = () => {
    if (!user) return;
    const payload = { userId: user.id, tenantId: tenantId! };
    acceptTerms.mutateAsync(payload);
  };

  return (
    <CustomModal
      centered
      open={firstLoginModal.visible}
      width={1100}
      hideCloseButton
      className={'fist-login-modal'}
      title={
        <span className="modal-title">
          {t('authentication.termsOfService')}
        </span>
      }
      footer={[
        <div key="agree" className="button-wrapper">
          <CustomButton
            size="large"
            width="200px"
            key="agree"
            htmlType="button"
            loading={isFetching || acceptTerms.isPending}
            aria-label="Agree btn"
            onClick={onAcceptTermsOfService}
          >
            {t('authentication.agree')}
          </CustomButton>
        </div>,
      ]}
    >
      <ModalBody className="modal-body-wrapper">
        <div className="content-wrapper">
          {pdfUrl && <PDFViewer pdfUrl={pdfUrl} />}
        </div>
      </ModalBody>
      <hr className="gray-line" />
    </CustomModal>
  );
};

export default memo(FirstLoginPromptModal);
